// Clause explanation library — long-tail SEO content.
// Each clause has plain-English explanation, examples, red flags, FAQ, and related links.

export type RelatedTool = { name: string; slug: string };
export type RelatedClause = { name: string; slug: string };

export type ClauseFAQ = { q: string; a: string };

export type Clause = {
  slug: string;
  category: "lease" | "work" | "employment" | "general";
  title: string; // SEO H1 / page title
  shortTitle: string; // for nav/cards
  metaTitle: string; // <title> ≤60 chars
  metaDescription: string; // ≤160 chars
  intro: string; // 1-2 sentence hook
  whatItIs: string; // ~100 words
  whyItMatters: string; // ~100 words
  sampleLanguage: string; // raw clause example
  sampleExplanation: string; // what it really means
  redFlags: string[]; // 3-6 bullets
  greenFlags: string[]; // fair / acceptable language
  negotiationTips: string[];
  faq: ClauseFAQ[];
  relatedTools: RelatedTool[];
  relatedClauses: RelatedClause[];
};

const T = {
  lease: { name: "Lease Decoder", slug: "lease-decoder" },
  nda: { name: "NDA Scanner", slug: "nda-scanner" },
  freelance: { name: "Freelancer Protector", slug: "freelancer-protector" },
  noncompete: { name: "Non-Compete Analyzer", slug: "non-compete-analyzer" },
  employment: { name: "Employment Agreement Checker", slug: "employment-agreement-checker" },
  contractor: { name: "Contractor Agreement Checker", slug: "contractor-agreement-checker" },
};

export const CLAUSES: Clause[] = [
  // ───────────────── LEASE ─────────────────
  {
    slug: "security-deposit-clause",
    category: "lease",
    title: "Security Deposit Clause: What It Means and How to Spot Unfair Terms",
    shortTitle: "Security Deposit Clause",
    metaTitle: "Security Deposit Clause Explained — legaldecoder",
    metaDescription:
      "What a security deposit clause really says, how much landlords can charge, and the red flags that signal you may never get your money back.",
    intro:
      "Your security deposit is often the largest single check you'll write when moving in — and the one most likely to disappear. Here's what to look for before you sign.",
    whatItIs:
      "A security deposit clause spells out how much you pay upfront, what the landlord can deduct it for, and when (and how) you get the balance back. Most U.S. states cap the amount (often 1–2 months' rent), require the deposit to sit in a separate account, and force the landlord to return it within 14–30 days of move-out with an itemized list of any deductions. The clause should also describe how the unit will be inspected and what counts as 'normal wear and tear' versus damage you can be charged for.",
    whyItMatters:
      "If the clause is vague, the landlord effectively writes the rules later. We routinely see leases that allow deductions for 'cleaning,' 'repainting,' or 'general upkeep' — costs that are usually the landlord's responsibility, not yours. A weak clause also lets a landlord miss the legal return deadline with no penalty. In many states, missing that deadline entitles you to two or three times the deposit back, but only if you know to ask.",
    sampleLanguage:
      "\"Tenant shall deposit with Landlord the sum of $4,500 as security for performance of Tenant's obligations. Landlord may apply the deposit to any amounts owed, including but not limited to cleaning, repainting, and general restoration, in Landlord's sole discretion.\"",
    sampleExplanation:
      "Three months' rent as a deposit is excessive in most states. 'Sole discretion' over deductions means you have no real recourse — and 'repainting' is almost always normal wear and tear. This clause should specify the legal cap, require itemized deductions, and exclude routine wear.",
    redFlags: [
      "Deposit exceeds your state's legal cap (often 1–2 months' rent)",
      "Landlord has 'sole discretion' to deduct — no itemized list required",
      "Includes 'cleaning' or 'repainting' as automatic deductions",
      "No deadline for returning the deposit, or deadline longer than state law allows",
      "No mention of a move-in inspection or written condition report",
      "Deposit is non-refundable in part or whole",
    ],
    greenFlags: [
      "Amount is at or below state cap",
      "Deposit held in a separate, sometimes interest-bearing account",
      "Itemized written statement required for any deduction",
      "Return deadline matches or beats state law",
      "Move-in/move-out inspection process described",
    ],
    negotiationTips: [
      "Ask for the deposit to be capped at one month's rent",
      "Request a written move-in condition report with photos before you pay",
      "Strike 'sole discretion' language — replace with 'reasonable, itemized'",
      "Confirm deposit return timeline in writing",
    ],
    faq: [
      {
        q: "Is my security deposit really refundable?",
        a: "Yes — by default, security deposits are refundable minus documented damage beyond normal wear and tear. Watch for 'non-refundable cleaning fees' or similar language that converts part of the deposit into a fee.",
      },
      {
        q: "How long does my landlord have to return it?",
        a: "Most U.S. states require return within 14–30 days of move-out, along with an itemized list of any deductions. Missing the deadline often triggers penalties of 2–3x the deposit.",
      },
      {
        q: "What counts as 'normal wear and tear'?",
        a: "Faded paint, minor carpet wear, small nail holes, and worn fixtures from regular use. Burns, large stains, holes in walls, and pet damage are typically chargeable.",
      },
    ],
    relatedTools: [T.lease],
    relatedClauses: [
      { name: "Early Termination Clause", slug: "early-termination-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
      { name: "Late Fees Clause", slug: "late-fees-clause" },
    ],
  },
  {
    slug: "early-termination-clause",
    category: "lease",
    title: "Early Termination Clause: How Much It Really Costs to Break a Lease",
    shortTitle: "Early Termination Clause",
    metaTitle: "Early Termination Clause Explained — legaldecoder",
    metaDescription:
      "What an early termination clause means, typical break-lease fees, and the red flags that lock you in financially even after you move out.",
    intro:
      "Life changes — jobs move, relationships shift, landlords get hostile. The early termination clause decides how much it costs to walk away.",
    whatItIs:
      "An early termination clause defines what happens if you leave before the lease ends. Fair clauses charge a fixed fee — typically 1–2 months' rent — and require 30–60 days' notice. Aggressive clauses make you pay rent until the unit is re-rented, with no obligation on the landlord to actually try. Some clauses also forfeit your deposit on top of the fee.",
    whyItMatters:
      "Without a clear termination clause, your only legal exit may be paying out the entire remaining lease. On a 12-month lease at $2,000/mo, walking out at month 4 could cost $16,000+. A reasonable clause caps your exposure to a known number so you can plan.",
    sampleLanguage:
      '"Tenant may terminate this Lease prior to the expiration date upon payment of all remaining rent through the end of the term, plus reletting costs and forfeiture of the security deposit."',
    sampleExplanation:
      "This is one of the worst termination clauses. You owe every remaining month, the landlord's costs, and your deposit — even if they re-rent the unit the next day. Push for a flat fee instead.",
    redFlags: [
      "Owe all remaining rent regardless of re-rental",
      "Security deposit automatically forfeited",
      "No defined notice period",
      "Landlord has no duty to mitigate (re-rent the unit)",
      "Termination fee exceeds 2 months' rent",
    ],
    greenFlags: [
      "Flat fee of 1–2 months' rent",
      "30–60 day written notice",
      "Landlord obligated to re-list the unit promptly",
      "Deposit returned per the security deposit clause",
    ],
    negotiationTips: [
      "Ask for a flat 'lease break fee' of one month's rent",
      "Add a military or job-relocation exception",
      "Require landlord to make 'reasonable efforts to re-rent'",
    ],
    faq: [
      {
        q: "Can I just leave and stop paying?",
        a: "Not without consequences. Most leases let the landlord sue for the remaining rent, and many states allow them to send the debt to collections, hurting your credit.",
      },
      {
        q: "Do landlords have to try to re-rent?",
        a: "In most U.S. states, yes — this is called the 'duty to mitigate damages.' But you may need to remind them in writing.",
      },
    ],
    relatedTools: [T.lease],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Auto-Renewal Clause", slug: "auto-renewal-clause" },
      { name: "Entry Notice Clause", slug: "entry-notice-clause" },
    ],
  },
  {
    slug: "auto-renewal-clause",
    category: "lease",
    title: "Auto-Renewal Clause: How Leases Quietly Trap You for Another Year",
    shortTitle: "Auto-Renewal Clause",
    metaTitle: "Auto-Renewal Clause Explained — legaldecoder",
    metaDescription:
      "How auto-renewal clauses lock you into another lease term, what notice rules apply, and how to opt out before the deadline.",
    intro:
      "You meant to move out. Then a clause you forgot existed renewed your lease for another year — at a higher rent.",
    whatItIs:
      "An auto-renewal clause says that if you don't notify the landlord by a specific date, the lease renews automatically — usually for the same term as the original. Some states require landlords to send a written reminder; many don't.",
    whyItMatters:
      "Miss the notice window and you can owe another full year of rent. Worse, many auto-renewal clauses also let the landlord raise the rent on renewal with no cap. Knowing the exact deadline turns a trap into a calendar reminder.",
    sampleLanguage:
      '"Unless either party gives written notice of non-renewal at least sixty (60) days prior to the expiration of the term, this Lease shall automatically renew for an additional twelve (12) month term at a rental rate determined by Landlord."',
    sampleExplanation:
      "The 60-day window is real — miss it by a day and you're locked in. Worse, the new rate is set by the landlord with no cap. Negotiate either a month-to-month rollover or a cap on renewal increases.",
    redFlags: [
      "No reminder requirement for the landlord",
      "Renewal term equal to original (often 12 months)",
      "No cap on rent increase at renewal",
      "Notice deadline more than 60 days before end of term",
      "Penalty for late notice exceeds normal rent",
    ],
    greenFlags: [
      "Auto-rolls to month-to-month, not another full term",
      "30-day notice window",
      "Rent increase capped at a fixed % or local CPI",
      "Landlord must send written reminder",
    ],
    negotiationTips: [
      "Convert auto-renewal to month-to-month after the initial term",
      "Cap renewal rent increases at 5%",
      "Ask landlord to send a written reminder 90 days out",
    ],
    faq: [
      {
        q: "Can I just ignore an auto-renewal?",
        a: "No. If you don't give notice in writing by the deadline, you're legally bound to the new term — even if you've already moved.",
      },
      {
        q: "Are auto-renewal clauses legal?",
        a: "Yes in most states, but several (like Florida, New York, and California) have notice requirements that protect tenants. Always check your state.",
      },
    ],
    relatedTools: [T.lease],
    relatedClauses: [
      { name: "Early Termination Clause", slug: "early-termination-clause" },
      { name: "Late Fees Clause", slug: "late-fees-clause" },
    ],
  },
  {
    slug: "entry-notice-clause",
    category: "lease",
    title: "Landlord Entry Notice Clause: Your Privacy Rights, Explained",
    shortTitle: "Entry Notice Clause",
    metaTitle: "Landlord Entry Notice Clause Explained — legaldecoder",
    metaDescription:
      "How much notice your landlord must give before entering your unit, what counts as an emergency, and the red flags that violate tenant rights.",
    intro:
      "Your apartment is your home — not your landlord's open house. The entry notice clause sets the rules.",
    whatItIs:
      "An entry notice clause defines when and how a landlord can enter your unit. In most U.S. states, landlords need 24–48 hours' written notice for non-emergencies (repairs, showings, inspections) and may only enter at reasonable hours.",
    whyItMatters:
      "Vague entry clauses let landlords show up unannounced or use 'inspection' as a pretext for harassment. A clear clause protects your privacy and gives you legal grounds if it's violated.",
    sampleLanguage:
      '"Landlord may enter the Premises at any reasonable time for purposes of inspection, repair, or showing to prospective tenants, with such notice as is practicable under the circumstances."',
    sampleExplanation:
      "'Notice as is practicable' is meaningless — that could be one minute. Insist on a specific notice period (24–48 hours) and a definition of 'reasonable hours' (e.g., 9am–6pm weekdays).",
    redFlags: [
      "No defined notice period",
      "Allows entry 'at any time'",
      "Doesn't restrict hours of entry",
      "Includes vague 'business purposes' as grounds",
      "No definition of 'emergency'",
    ],
    greenFlags: [
      "24–48 hours written notice for non-emergencies",
      "Entry only during reasonable hours",
      "Emergency narrowly defined (fire, flood, gas leak)",
      "Method of notice specified (text, email, posted)",
    ],
    negotiationTips: [
      "Insist on 24-hour written notice minimum",
      "Limit entry to weekday business hours unless you consent",
      "Define 'emergency' tightly in the clause",
    ],
    faq: [
      {
        q: "Can my landlord enter without telling me?",
        a: "Only in true emergencies in most states (fire, flood, gas leak). Otherwise, 24–48 hours written notice is standard.",
      },
      {
        q: "What if my landlord enters illegally?",
        a: "You may have grounds for a lease violation, harassment claim, or even early termination depending on the state. Document every incident in writing.",
      },
    ],
    relatedTools: [T.lease],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Early Termination Clause", slug: "early-termination-clause" },
    ],
  },
  {
    slug: "late-fees-clause",
    category: "lease",
    title: "Late Fees Clause: When Rent Penalties Cross the Legal Line",
    shortTitle: "Late Fees Clause",
    metaTitle: "Late Fees Clause Explained — legaldecoder",
    metaDescription:
      "How late fee clauses work in residential leases, what amounts are legal, and the red flags that signal predatory penalties.",
    intro:
      "A late fee should cover the landlord's hassle — not double as a profit center. Here's where the line is.",
    whatItIs:
      "A late fees clause defines what happens if rent isn't paid on time: the grace period (usually 3–5 days), the fee amount (typically 5% of monthly rent or a flat $25–$75), and any compounding daily charges.",
    whyItMatters:
      "Many states cap late fees as a percentage of rent and prohibit daily compounding. Aggressive clauses can turn a single missed paycheck into hundreds of extra dollars and an eviction filing.",
    sampleLanguage:
      '"If rent is not received by the 1st of the month, Tenant shall pay a late fee of $100 plus $25 per day until paid in full. Repeated late payment shall constitute a material breach."',
    sampleExplanation:
      "No grace period and a daily compounding fee — likely illegal in many states. A fair clause allows 3–5 days grace and caps the late fee at ~5% of monthly rent.",
    redFlags: [
      "No grace period",
      "Daily compounding fees",
      "Late fee exceeds 5–10% of monthly rent",
      "First late payment triggers eviction notice",
      "Late fees applied before due date posted",
    ],
    greenFlags: [
      "3–5 day grace period",
      "Flat fee or capped percentage",
      "Single charge, not daily",
      "Notice required before eviction",
    ],
    negotiationTips: [
      "Ask for a 5-day grace period",
      "Cap the fee at $50 or 5% of rent — whichever is lower",
      "Strike daily compounding language",
    ],
    faq: [
      {
        q: "Are there limits on late fees?",
        a: "Yes — many states cap late fees as a percentage of monthly rent (often 5%) and prohibit excessive daily charges. Check your state's specific rules.",
      },
      {
        q: "Can a single late payment get me evicted?",
        a: "In most states, the landlord must serve a 'pay or quit' notice (3–5 days) before filing for eviction. One late payment alone usually isn't grounds.",
      },
    ],
    relatedTools: [T.lease],
    relatedClauses: [
      { name: "Security Deposit Clause", slug: "security-deposit-clause" },
      { name: "Early Termination Clause", slug: "early-termination-clause" },
    ],
  },
  // ───────────────── WORK ─────────────────
  {
    slug: "confidentiality-clause",
    category: "work",
    title: "Confidentiality Clause: What You Can and Can't Say After Signing an NDA",
    shortTitle: "Confidentiality Clause",
    metaTitle: "Confidentiality Clause Explained — legaldecoder",
    metaDescription:
      "How confidentiality clauses define 'confidential information,' how long they last, and the red flags that gag you well beyond what's reasonable.",
    intro:
      "Most NDAs are reasonable. Some quietly turn into lifetime gag orders. Here's how to tell the difference.",
    whatItIs:
      "A confidentiality clause defines what information is 'confidential,' who you can share it with, how you must protect it, and how long the obligation lasts. Reasonable clauses limit confidentiality to specific business information for 2–5 years.",
    whyItMatters:
      "Overbroad clauses can cover anything you 'learn' on the job — including your own skills and ideas. Indefinite duration means the obligation outlives the relationship, the company, and sometimes your career.",
    sampleLanguage:
      '"Recipient agrees that all information of any kind disclosed by or learned about Discloser shall be considered Confidential Information and shall not be disclosed for any purpose, in perpetuity."',
    sampleExplanation:
      "'Information of any kind' and 'in perpetuity' are massive red flags. Confidentiality should apply only to specifically marked or clearly proprietary information, with a defined end date (typically 2–5 years).",
    redFlags: [
      "Definition covers 'any information' rather than marked confidential material",
      "Indefinite or 'perpetual' duration",
      "No exceptions for publicly available info",
      "Includes ideas you brought into the company",
      "Covers your own general skills and knowledge",
    ],
    greenFlags: [
      "Specific definition of 'Confidential Information'",
      "2–5 year duration after relationship ends",
      "Standard exceptions: public knowledge, prior knowledge, independent development",
      "Mutual obligations (both sides protect each other's info)",
    ],
    negotiationTips: [
      "Cap duration at 3 years post-termination",
      "Add standard exceptions (public domain, prior knowledge, court-ordered disclosure)",
      "Insist on mutual obligations if you'll share info too",
    ],
    faq: [
      {
        q: "How long does an NDA last?",
        a: "Standard is 2–5 years after the relationship ends. Trade secrets can be perpetual, but only true trade secrets — not 'all information.'",
      },
      {
        q: "Can an NDA stop me from talking about harassment?",
        a: "Several states (CA, NY, NJ) have laws making it unlawful to use NDAs to silence harassment or discrimination claims. Federal Speak Out Act covers sexual harassment.",
      },
    ],
    relatedTools: [T.nda, T.freelance],
    relatedClauses: [
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
  },
  {
    slug: "non-compete-clause",
    category: "work",
    title: "Non-Compete Clause: How Much of Your Career Are You Really Signing Away?",
    shortTitle: "Non-Compete Clause",
    metaTitle: "Non-Compete Clause Explained — legaldecoder",
    metaDescription:
      "What non-compete clauses actually restrict, where they're enforceable, and the red flags that block you from working in your field for years.",
    intro:
      "A non-compete clause can quietly determine where you can work, who you can work for, and for how long after you leave.",
    whatItIs:
      "A non-compete clause restricts you from working for competitors, starting a competing business, or operating in a defined geographic area for a set period after your employment ends. Enforceability varies wildly by state.",
    whyItMatters:
      "California, North Dakota, Oklahoma, and Minnesota largely refuse to enforce non-competes for employees. Other states only enforce 'reasonable' restrictions in scope, geography, and duration. The clause may be unenforceable — but most workers don't know that and comply anyway.",
    sampleLanguage:
      '"For a period of 24 months following termination, Employee shall not directly or indirectly engage in any business that competes with Company anywhere in the United States."',
    sampleExplanation:
      "24 months and nationwide is overly broad in most states and likely unenforceable. Reasonable scope is typically 6–12 months and limited to a specific city, industry, or client list.",
    redFlags: [
      "Duration over 12 months",
      "Geographic scope larger than where you actually work",
      "'Any business that competes' — undefined",
      "Triggers even if you're laid off",
      "No consideration paid for the restriction",
    ],
    greenFlags: [
      "6–12 month duration",
      "Limited geography (city or specific clients)",
      "Industry/role narrowly defined",
      "Doesn't apply if employer terminates without cause",
      "Clear consideration (signing bonus, severance)",
    ],
    negotiationTips: [
      "Cut duration to 6–12 months",
      "Limit to specific named competitors or clients",
      "Add 'severance pay during restricted period' clause",
      "Require employer-paid release if they terminate you",
    ],
    faq: [
      {
        q: "Are non-competes enforceable?",
        a: "Depends entirely on your state. California voids almost all employee non-competes. Most other states require 'reasonable' scope, time, and geography to enforce.",
      },
      {
        q: "What if I'm laid off — does it still apply?",
        a: "Often yes, unless the clause says otherwise. Negotiate language that voids the non-compete if you're terminated without cause.",
      },
    ],
    relatedTools: [T.noncompete, T.nda],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
  },
  {
    slug: "ip-assignment-clause",
    category: "work",
    title: "IP Assignment Clause: When Your Ideas Stop Being Yours",
    shortTitle: "IP Assignment Clause",
    metaTitle: "IP Assignment Clause Explained — legaldecoder",
    metaDescription:
      "How IP assignment clauses transfer ownership of your work, what 'work for hire' really means, and how to protect personal projects from your employer.",
    intro:
      "Sign the wrong IP clause and the side project you build at 2 a.m. on a Saturday could legally belong to your employer.",
    whatItIs:
      "An IP assignment clause transfers ownership of inventions, code, designs, and other work product from you to the company. Reasonable clauses cover work created on the job using company resources. Aggressive ones cover everything you create during the relationship — including personal projects.",
    whyItMatters:
      "If you have a side hustle, write open-source code, or have invention ideas predating the job, an overbroad IP clause can swallow them. Several states (California, Washington, Illinois, etc.) have laws limiting how far an employer can reach.",
    sampleLanguage:
      '"Employee hereby assigns to Company all inventions, ideas, and works of authorship conceived during the term of employment, regardless of whether developed using Company resources or during working hours."',
    sampleExplanation:
      "This grabs your personal work too. In California (Labor Code 2870) and similar states, this clause is partially unenforceable — but the employer may still claim it. Add a personal projects exclusion in writing.",
    redFlags: [
      "Covers anything created during employment, even on personal time",
      "No exclusion for prior inventions",
      "No carve-out for unrelated personal projects",
      "Extends past termination",
      "Includes 'ideas' not just deliverables",
    ],
    greenFlags: [
      "Limited to work created using company resources",
      "Excludes work unrelated to company business",
      "Schedule of pre-existing IP (your prior inventions)",
      "Ends at termination",
    ],
    negotiationTips: [
      "Attach a 'Prior Inventions' schedule listing your existing work",
      "Add carve-out for projects unrelated to company business and built off-hours",
      "Cite your state's labor code if applicable",
    ],
    faq: [
      {
        q: "Does my employer own my side projects?",
        a: "Possibly — depending on the IP clause and your state. Many states protect personal projects unrelated to company business and built without company resources.",
      },
      {
        q: "What's a 'Prior Inventions' schedule?",
        a: "A list you attach to your employment agreement listing inventions you owned before the job. They stay yours.",
      },
    ],
    relatedTools: [T.nda, T.freelance],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
    ],
  },
  {
    slug: "indemnification-clause",
    category: "work",
    title: "Indemnification Clause: Who Pays When Something Goes Wrong",
    shortTitle: "Indemnification Clause",
    metaTitle: "Indemnification Clause Explained — legaldecoder",
    metaDescription:
      "What an indemnification clause means in plain English, how it shifts financial risk, and the red flags that put you on the hook for the company's mistakes.",
    intro:
      "Indemnification clauses are legalese for 'who pays the lawyer.' Get this wrong as a freelancer or contractor and one lawsuit can wipe you out.",
    whatItIs:
      "An indemnification clause says one party will reimburse the other for losses, claims, or legal fees caused by certain events. In freelance and contractor agreements, the client often demands you indemnify them — meaning you cover their legal bills if anything goes wrong.",
    whyItMatters:
      "A one-sided indemnity can make you personally liable for far more than the contract is worth. Without a cap, a six-figure lawsuit can land in your lap from a $5,000 project. Mutual indemnity and a damages cap are essential protections.",
    sampleLanguage:
      '"Contractor shall indemnify, defend, and hold harmless Client from any and all claims, damages, losses, and expenses, including attorneys\' fees, arising from or related to the Services, regardless of cause."',
    sampleExplanation:
      "'Regardless of cause' means you pay even if the client caused the problem. There's no cap, no mutual obligation, and the trigger ('arising from or related to') is impossibly broad.",
    redFlags: [
      "One-sided (only you indemnify the client)",
      "No cap on liability",
      "Triggers on 'any claim,' even unrelated ones",
      "Includes the client's own negligence",
      "Includes consequential or punitive damages",
    ],
    greenFlags: [
      "Mutual indemnity",
      "Liability capped at fees paid",
      "Triggers limited to your gross negligence or IP infringement",
      "Excludes the other party's negligence",
      "Excludes consequential damages",
    ],
    negotiationTips: [
      "Insist on mutual indemnification",
      "Cap your liability at total fees paid (or 1–2x)",
      "Limit triggers to your willful misconduct or IP infringement",
      "Exclude consequential damages explicitly",
    ],
    faq: [
      {
        q: "Do I really need indemnity insurance?",
        a: "If you sign one-sided indemnity clauses regularly, yes — professional liability (E&O) insurance is strongly recommended for freelancers and consultants.",
      },
      {
        q: "Can I just refuse to indemnify?",
        a: "Yes, you can negotiate. Many clients will accept mutual indemnity, capped liability, or remove the clause entirely if pushed.",
      },
    ],
    relatedTools: [T.freelance, T.nda],
    relatedClauses: [
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
  },
  {
    slug: "termination-for-convenience-clause",
    category: "work",
    title: "Termination for Convenience Clause: When Either Side Can Walk Away",
    shortTitle: "Termination for Convenience Clause",
    metaTitle: "Termination for Convenience Clause — legaldecoder",
    metaDescription:
      "How termination for convenience clauses let clients cancel any time, what notice they must give, and how to protect your income.",
    intro:
      "'Termination for convenience' sounds polite. In freelance contracts it usually means: the client can cancel any project, any time, for any reason — and you may not get paid.",
    whatItIs:
      "This clause lets one party (usually the client) end the contract without breach, often on short notice. Reasonable clauses require 14–30 days' notice and payment for work completed. Aggressive clauses let the client cancel immediately and pay nothing for in-progress work.",
    whyItMatters:
      "If you've turned down other work or staffed up for a project, a same-day cancellation with no kill fee can be devastating. A fair clause requires meaningful notice and pays for work-in-progress.",
    sampleLanguage:
      '"Client may terminate this Agreement at any time, for any reason, with no obligation to pay for work not yet delivered."',
    sampleExplanation:
      "Total client power, zero protection for you. Add a notice period (14–30 days), payment for work-in-progress, and a kill fee if cancelled before milestone delivery.",
    redFlags: [
      "Immediate termination with no notice",
      "No payment for work in progress",
      "No kill fee or cancellation fee",
      "Only the client can terminate",
      "Forfeits already-paid deposits",
    ],
    greenFlags: [
      "14–30 day written notice required",
      "Payment for all completed and in-progress work",
      "Kill fee equal to next milestone or % of remaining contract",
      "Mutual termination rights",
    ],
    negotiationTips: [
      "Require 30 days written notice",
      "Get paid for all hours worked through termination date",
      "Add a 25–50% kill fee on cancelled milestones",
      "Make termination rights mutual",
    ],
    faq: [
      {
        q: "Can a client really cancel anytime?",
        a: "If you signed a termination for convenience clause without protections, yes. Always negotiate notice and kill fee terms upfront.",
      },
      {
        q: "What's a 'kill fee'?",
        a: "A pre-agreed payment owed if the client cancels mid-project. Common in creative work — typically 25–50% of the remaining contract value.",
      },
    ],
    relatedTools: [T.freelance],
    relatedClauses: [
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
    ],
  },
  // ───────────────── EMPLOYMENT ─────────────────
  {
    slug: "non-solicit-clause",
    category: "employment",
    title: "Non-Solicit Clause: When You Can't Take Clients or Coworkers With You",
    shortTitle: "Non-Solicit Clause",
    metaTitle: "Non-Solicit Clause Explained — legaldecoder",
    metaDescription:
      "What a non-solicitation clause restricts, how courts enforce it, and the red flags that quietly act as a back-door non-compete.",
    intro:
      "A non-solicit clause won't stop you from taking a new job — but it can stop your old clients and coworkers from following you there.",
    whatItIs:
      "A non-solicit clause restricts you from contacting former clients, customers, or coworkers for a defined period after leaving. It usually lasts 6–24 months and can apply to soliciting business, recruiting employees, or both.",
    whyItMatters:
      "Non-solicits are often more enforceable than non-competes — even in California, narrowly tailored employee non-solicits can sometimes hold. A broad non-solicit can effectively bar you from your industry without ever using the word 'compete.'",
    sampleLanguage:
      '"For 24 months following termination, Employee shall not directly or indirectly solicit, contact, or do business with any current or former customer of Company, nor recruit any current or former employee of Company."',
    sampleExplanation:
      "'Former customer' with no time limit and 24 months is overbroad. Reasonable scope is 6–12 months, only customers you personally worked with, and only active customers — not 'former.'",
    redFlags: [
      "Duration over 12 months",
      "Covers customers you never personally worked with",
      "Includes 'former' customers with no time bound",
      "Bars accepting unsolicited business",
      "Covers all employees, not just direct reports",
    ],
    greenFlags: [
      "6–12 month duration",
      "Limited to customers you worked with in the last 12 months",
      "Allows passive/unsolicited contact",
      "Limited to employees you directly managed",
    ],
    negotiationTips: [
      "Cap at 12 months",
      "Limit to clients you personally serviced",
      "Carve out passive marketing and unsolicited inbound",
    ],
    faq: [
      {
        q: "Can a non-solicit really stop me from working with my old clients?",
        a: "Yes — even if you didn't reach out first. Many non-solicits cover any business with covered customers, regardless of who initiated contact.",
      },
      {
        q: "Are non-solicits enforceable in California?",
        a: "Customer non-solicits are mostly unenforceable in California after AMN Healthcare v. Aya. Employee non-solicits are still tested case by case.",
      },
    ],
    relatedTools: [T.employment, T.noncompete],
    relatedClauses: [
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
    ],
  },
  {
    slug: "severance-clause",
    category: "employment",
    title: "Severance Clause: What You Actually Get If You're Let Go",
    shortTitle: "Severance Clause",
    metaTitle: "Severance Clause Explained — legaldecoder",
    metaDescription:
      "What a severance clause guarantees, how it's calculated, and the release language that quietly waives your right to sue.",
    intro:
      "Severance can be a soft landing — or a trap that buys your silence for less than you're owed.",
    whatItIs:
      "A severance clause spells out what you receive if your employment ends: pay (often weeks per year of service), benefits continuation, and what you must give up in return — usually a release of legal claims and sometimes new restrictive covenants.",
    whyItMatters:
      "The 'release' inside a severance agreement can waive discrimination, harassment, and wage claims worth far more than the severance itself. Federal law (OWBPA) requires 21–45 days to consider releases of age claims and 7 days to revoke.",
    sampleLanguage:
      '"In exchange for severance, Employee fully releases Company from any and all claims, known or unknown, and agrees to a 12-month non-compete and non-disparagement obligation."',
    sampleExplanation:
      "Watch for new restrictive covenants slipped into the severance — non-competes, non-solicits, or non-disparagement you never agreed to before. Negotiate them out, or get more money.",
    redFlags: [
      "Less than 1–2 weeks per year of service",
      "Adds new non-compete or non-solicit",
      "Broad non-disparagement clause",
      "No 21-day consideration period for age releases",
      "Forfeits earned bonuses or unvested equity",
    ],
    greenFlags: [
      "2+ weeks pay per year of service",
      "Continued health benefits (COBRA reimbursement)",
      "Mutual non-disparagement",
      "Carve-out for whistleblower claims",
      "Earned bonus and accrued PTO paid",
    ],
    negotiationTips: [
      "Negotiate severance UP — first offer is rarely the last",
      "Strike new restrictive covenants",
      "Demand mutual non-disparagement",
      "Get earned bonus and PTO paid separately",
    ],
    faq: [
      {
        q: "Do I have to sign a severance agreement?",
        a: "No. Severance is almost always conditional on signing a release. You can negotiate or refuse — but you'll likely lose the severance pay.",
      },
      {
        q: "Can I sue after taking severance?",
        a: "Usually no — most severance includes a broad release. Exceptions include unwaivable claims like workers' comp, unemployment, and (sometimes) whistleblower retaliation.",
      },
    ],
    relatedTools: [T.employment],
    relatedClauses: [
      { name: "Non-Compete Clause", slug: "non-compete-clause" },
      { name: "Arbitration Clause", slug: "arbitration-clause" },
    ],
  },
  {
    slug: "at-will-employment-clause",
    category: "employment",
    title: "At-Will Employment Clause: What It Really Means for Your Job Security",
    shortTitle: "At-Will Employment Clause",
    metaTitle: "At-Will Employment Explained — legaldecoder",
    metaDescription:
      "What at-will employment means, what protections you still have, and the language that quietly waives them.",
    intro:
      "'At-will' sounds neutral. In practice it means your employer can fire you for almost any reason, on any day, with no notice.",
    whatItIs:
      "An at-will clause states that either the employee or employer can terminate the employment at any time, with or without cause or notice. It's the default rule in 49 U.S. states (Montana excepted).",
    whyItMatters:
      "At-will doesn't mean unprotected — anti-discrimination law, retaliation law, and contract exceptions still apply. But aggressive at-will language tries to waive even those protections or eliminate any 'implied' contracts created by handbooks or verbal promises.",
    sampleLanguage:
      '"Employment is strictly at-will. No representation by any manager creates an implied contract of continued employment. Employee waives any claim arising from termination."',
    sampleExplanation:
      "The 'waives any claim' language tries to eliminate discrimination and retaliation suits — usually unenforceable, but a red flag for the employer's posture.",
    redFlags: [
      "Tries to waive statutory claims",
      "Disclaims all handbook policies",
      "Permits termination 'including for any unlawful reason' (yes, it happens)",
      "Eliminates any notice or final-pay obligation beyond state law",
    ],
    greenFlags: [
      "Standard at-will language only",
      "Confirms compliance with anti-discrimination law",
      "Notes any progressive discipline or notice policies",
    ],
    negotiationTips: [
      "Negotiate a 'for cause' definition or notice period",
      "Get a guaranteed severance amount on without-cause termination",
      "Strike language disclaiming statutory protections",
    ],
    faq: [
      {
        q: "Can I be fired for any reason?",
        a: "Almost — but not for an illegal reason (race, sex, age, disability, religion, retaliation for protected activity, etc.).",
      },
      {
        q: "Can I negotiate out of at-will?",
        a: "Rarely as a standalone, but you can negotiate severance on without-cause termination, which has the same practical effect.",
      },
    ],
    relatedTools: [T.employment],
    relatedClauses: [
      { name: "Severance Clause", slug: "severance-clause" },
      { name: "Arbitration Clause", slug: "arbitration-clause" },
    ],
  },
  {
    slug: "arbitration-clause",
    category: "employment",
    title: "Arbitration Clause: How You Quietly Lose Your Right to Sue",
    shortTitle: "Arbitration Clause",
    metaTitle: "Arbitration Clause Explained — legaldecoder",
    metaDescription:
      "How arbitration clauses force disputes out of court, what you give up by signing, and the red flags every employee should watch for.",
    intro:
      "An arbitration clause swaps your right to a public courtroom for a private, employer-friendly forum — usually for life.",
    whatItIs:
      "An arbitration clause requires you to resolve disputes through private arbitration instead of court. It typically waives jury trial and class-action rights, sets the arbitrator and rules, and allocates fees.",
    whyItMatters:
      "Arbitration favors employers statistically. Studies show employees win less often and recover less when they do. Many clauses also force you to pay a share of arbitration fees — sometimes thousands of dollars per case.",
    sampleLanguage:
      '"Any dispute arising out of or related to this Agreement shall be resolved by binding arbitration. Employee waives the right to a jury trial and to participate in any class or collective action."',
    sampleExplanation:
      "Class-action waivers compound the harm — they prevent groups of employees from joining together. The 2022 Ending Forced Arbitration of Sexual Assault and Sexual Harassment Act voids these for those specific claims.",
    redFlags: [
      "Class action waiver",
      "Employee pays arbitration fees",
      "Employer chooses the arbitrator unilaterally",
      "Confidentiality clause prevents discussing the dispute",
      "No carve-out for sexual harassment / assault (federally void anyway)",
    ],
    greenFlags: [
      "Employer pays all arbitration costs above court filing fees",
      "Mutual obligation (employer must arbitrate too)",
      "Neutral arbitration provider (AAA, JAMS) with employee-protective rules",
      "Carve-outs for injunctive relief, IP, and statutory whistleblower claims",
    ],
    negotiationTips: [
      "Strike class action waivers if possible",
      "Demand employer pays all arbitration fees",
      "Insist on AAA or JAMS employment rules",
    ],
    faq: [
      {
        q: "Can I refuse to sign an arbitration clause?",
        a: "Sometimes — many employers will negotiate, especially for senior roles. Otherwise, signing is often a condition of employment.",
      },
      {
        q: "Does arbitration apply to harassment claims?",
        a: "No — federal law (2022) voids forced arbitration for sexual assault and sexual harassment claims regardless of what the contract says.",
      },
    ],
    relatedTools: [T.employment, T.freelance],
    relatedClauses: [
      { name: "Jurisdiction Clause", slug: "jurisdiction-clause" },
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
    ],
  },
  // ───────────────── GENERAL CONTRACT ─────────────────
  {
    slug: "limitation-of-liability-clause",
    category: "general",
    title: "Limitation of Liability Clause: Who Pays When the Worst Happens",
    shortTitle: "Limitation of Liability Clause",
    metaTitle: "Limitation of Liability Clause — legaldecoder",
    metaDescription:
      "How limitation of liability clauses cap damages, which damages they exclude, and the red flags that leave you fully exposed.",
    intro:
      "Limitation of liability is the math behind every contract dispute. Get the cap right and you sleep at night.",
    whatItIs:
      "This clause caps how much one party can recover from the other if something goes wrong. It usually excludes 'consequential' damages (lost profits, business interruption) and sets a dollar cap, often tied to fees paid under the contract.",
    whyItMatters:
      "Without a cap, a small contract can produce a giant lawsuit. With a one-sided cap (limiting only their liability, not yours), you absorb all the risk.",
    sampleLanguage:
      '"In no event shall Provider\'s total liability exceed the fees paid in the prior twelve (12) months, and Provider shall not be liable for any indirect, incidental, or consequential damages."',
    sampleExplanation:
      "Reasonable cap (12 months of fees) and standard consequential damages exclusion. But check whether the cap is mutual — if only the provider is capped, you're carrying all the downside.",
    redFlags: [
      "Cap applies to only one party",
      "Cap below total fees paid",
      "Excludes liability for the other party's gross negligence or willful misconduct",
      "Excludes liability for IP infringement / data breach",
    ],
    greenFlags: [
      "Mutual cap, both sides protected",
      "Cap = 12 months of fees (or higher)",
      "Carve-outs for IP infringement, data breach, gross negligence, indemnity",
      "Standard exclusion of indirect / consequential damages",
    ],
    negotiationTips: [
      "Make the cap mutual",
      "Add carve-outs for IP, breach of confidentiality, gross negligence",
      "Raise cap for high-risk engagements",
    ],
    faq: [
      {
        q: "What are 'consequential damages'?",
        a: "Indirect losses caused by a breach — lost profits, lost customers, business interruption. Most contracts exclude them, which dramatically lowers exposure.",
      },
    ],
    relatedTools: [T.freelance, T.contractor, T.employment],
    relatedClauses: [
      { name: "Indemnification Clause", slug: "indemnification-clause" },
      { name: "Force Majeure Clause", slug: "force-majeure-clause" },
    ],
  },
  {
    slug: "force-majeure-clause",
    category: "general",
    title: "Force Majeure Clause: When 'Acts of God' Excuse Performance",
    shortTitle: "Force Majeure Clause",
    metaTitle: "Force Majeure Clause Explained — legaldecoder",
    metaDescription:
      "What force majeure covers, how pandemics and supply-chain failures changed the standard, and the red flags to watch for.",
    intro:
      "Force majeure is the escape hatch for events nobody could have predicted. Read who actually gets to use it.",
    whatItIs:
      "A force majeure clause excuses performance when an extraordinary event (war, natural disaster, pandemic, government order) makes it impossible. Modern clauses also list pandemic, cyberattack, and supply-chain failure explicitly.",
    whyItMatters:
      "Post-2020, courts read these clauses narrowly — if the listed events don't include the situation, you may still owe performance. The clause often only protects one party (typically the supplier).",
    sampleLanguage:
      '"Neither party shall be liable for any failure to perform due to causes beyond its reasonable control, including acts of God, war, pandemic, or government action."',
    sampleExplanation:
      "Mutual and lists pandemic — reasonable. But check the notice requirement, the duration of suspension, and whether either side can terminate after a long disruption.",
    redFlags: [
      "Only one side can invoke force majeure",
      "No pandemic, cyberattack, or supply-chain language",
      "No notice requirement",
      "No termination right after a long disruption",
    ],
    greenFlags: [
      "Mutual",
      "Modern event list (pandemic, cyber, supply chain, gov action)",
      "Notice requirement (e.g., 10 days)",
      "Right to terminate if disruption exceeds 30–60 days",
    ],
    negotiationTips: [
      "Make it mutual",
      "Add modern events explicitly",
      "Add a termination right after extended disruption",
    ],
    faq: [
      {
        q: "Does force majeure cover COVID-style pandemics?",
        a: "Only if the clause says so. After 2020, courts have insisted on specific language — generic 'acts of God' is no longer enough.",
      },
    ],
    relatedTools: [T.freelance, T.contractor],
    relatedClauses: [
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
    ],
  },
  {
    slug: "jurisdiction-clause",
    category: "general",
    title: "Jurisdiction Clause: Which Court Decides If You Sue",
    shortTitle: "Jurisdiction Clause",
    metaTitle: "Jurisdiction Clause Explained — legaldecoder",
    metaDescription:
      "How jurisdiction and venue clauses choose where lawsuits happen, and why agreeing to a faraway state can effectively kill your right to sue.",
    intro:
      "If your contract says disputes are heard in Delaware and you live in Texas, you may never actually sue.",
    whatItIs:
      "A jurisdiction (or 'choice of law / venue') clause specifies which state's law governs the contract and which court hears disputes. Many large companies pick their home state to make litigation expensive for the other side.",
    whyItMatters:
      "Travel costs, local counsel fees, and unfamiliar court rules can make a $50,000 claim cost $150,000 to litigate. The result: you settle for less or never file.",
    sampleLanguage:
      '"This Agreement shall be governed by the laws of Delaware. Any dispute shall be brought exclusively in the state or federal courts located in Wilmington, Delaware."',
    sampleExplanation:
      "Standard for Delaware-incorporated companies, but unfair if you live across the country. Negotiate for your home state, the defendant's home state, or a neutral forum.",
    redFlags: [
      "Exclusive jurisdiction in a state you've never been to",
      "Choice of law that voids consumer protection in your state",
      "Combined with arbitration clause requiring travel to that state",
    ],
    greenFlags: [
      "Either party's home state",
      "Neutral state (Delaware acceptable for both businesses)",
      "Allows online or remote arbitration",
    ],
    negotiationTips: [
      "Ask for your home state",
      "Or compromise on whichever party is the defendant's home state",
      "Or strike exclusivity (allow either forum)",
    ],
    faq: [
      {
        q: "Can I ignore the jurisdiction clause and sue locally?",
        a: "Usually not — courts enforce these unless they're 'unreasonable.' That's a high bar.",
      },
    ],
    relatedTools: [T.freelance, T.employment],
    relatedClauses: [
      { name: "Arbitration Clause", slug: "arbitration-clause" },
      { name: "Limitation of Liability Clause", slug: "limitation-of-liability-clause" },
    ],
  },
  {
    slug: "auto-renewal-subscription-clause",
    category: "general",
    title: "Auto-Renewal (Subscription) Clause: How Contracts Quietly Renew Themselves",
    shortTitle: "Auto-Renewal (Subscription) Clause",
    metaTitle: "Auto-Renewal Clause (Subscriptions) — legaldecoder",
    metaDescription:
      "How auto-renewal clauses lock you into another term, when they're enforceable, and the notice you must give to opt out.",
    intro:
      "SaaS, gym memberships, vendor agreements — almost every recurring contract includes an auto-renewal trap.",
    whatItIs:
      "An auto-renewal clause renews the contract for another full term unless you give notice by a specific deadline (often 30–90 days before expiration). Several states (CA, NY, OR) require special opt-out disclosures.",
    whyItMatters:
      "Miss the notice and you owe another year. Pricing on renewal is often unilateral — the vendor sets the new rate.",
    sampleLanguage:
      '"This Agreement shall automatically renew for successive 12-month terms unless either party provides written notice of non-renewal at least 90 days prior to the end of the then-current term."',
    sampleExplanation:
      "90 days is a long window. Calendar it the moment you sign — and request the renewal price in writing 60 days before.",
    redFlags: [
      "Notice window > 60 days",
      "No cap on price increases at renewal",
      "Multi-year auto-renewal terms",
      "No required reminder from the vendor",
    ],
    greenFlags: [
      "30-day notice window",
      "Renewal price tied to CPI or capped %",
      "Vendor must send written reminder",
      "Month-to-month rollover after first term",
    ],
    negotiationTips: [
      "Convert to month-to-month after initial term",
      "Cap renewal increases",
      "Require written reminder 60 days before deadline",
    ],
    faq: [
      {
        q: "Are auto-renewal clauses enforceable?",
        a: "Yes in most states, with consumer disclosures required in CA, NY, OR, and others. B2B contracts have fewer protections.",
      },
    ],
    relatedTools: [T.contractor, T.freelance],
    relatedClauses: [
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Auto-Renewal Clause (Lease)", slug: "auto-renewal-clause" },
    ],
  },
  {
    slug: "assignment-clause",
    category: "general",
    title: "Assignment Clause: When Your Contract Gets Sold to Someone Else",
    shortTitle: "Assignment Clause",
    metaTitle: "Assignment Clause Explained — legaldecoder",
    metaDescription:
      "How assignment clauses let one party transfer the contract to a third party — and the language that lets it happen without your consent.",
    intro:
      "You signed with one company. After an acquisition, you may suddenly be doing business with a competitor.",
    whatItIs:
      "An assignment clause governs whether either party can transfer their rights and obligations under the contract to a third party. It commonly distinguishes between assignment to an affiliate, in a merger or acquisition, or to an unrelated third party.",
    whyItMatters:
      "Without restrictions, your counterparty can sell the contract — and your data, IP licenses, or service obligations — to anyone. An anti-assignment clause keeps you in control of who you're actually contracting with.",
    sampleLanguage:
      '"Neither party may assign this Agreement without the other party\'s prior written consent, except that either party may assign to an affiliate or successor in interest in connection with a merger, acquisition, or sale of substantially all assets."',
    sampleExplanation:
      "Reasonable. Both sides need consent except for normal corporate events. Strike the M&A carve-out only if your contract is highly personal (specific person delivering services).",
    redFlags: [
      "Other side can assign freely; you can't",
      "No notice required on assignment",
      "Assignment to competitors permitted without consent",
    ],
    greenFlags: [
      "Mutual consent required",
      "Notice on assignment",
      "Carve-outs limited to normal M&A events",
    ],
    negotiationTips: [
      "Make the consent requirement mutual",
      "Add a termination right if assigned to a direct competitor",
    ],
    faq: [
      {
        q: "What's the difference between assignment and subcontracting?",
        a: "Assignment transfers the whole contract. Subcontracting hires someone else to perform part of it — the original party stays liable.",
      },
    ],
    relatedTools: [T.freelance, T.contractor],
    relatedClauses: [
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
    ],
  },
  {
    slug: "payment-terms-clause",
    category: "general",
    title: "Payment Terms Clause: Net-30, Net-60, and the Numbers That Hurt You",
    shortTitle: "Payment Terms Clause",
    metaTitle: "Payment Terms Clause Explained — legaldecoder",
    metaDescription:
      "How payment terms work in contracts, what net-30 / net-60 / net-90 really cost, and the red flags that delay or shrink your payment.",
    intro:
      "The most expensive line in any contract is often the one that says when — and if — you actually get paid.",
    whatItIs:
      "Payment terms specify how much, when, and how you're paid: invoice cycles, due dates (net-30, net-60, net-90), late fees, dispute windows, and what counts as a valid invoice. Long payment terms function as zero-interest loans you give the client.",
    whyItMatters:
      "A net-90 contract on a $20,000 project means you finance $20,000 of the client's operations for three months. Late fee clauses, dispute windows, and 'pay-when-paid' language can stretch that even further.",
    sampleLanguage:
      '"Client shall pay all undisputed invoices within ninety (90) days of receipt. Client may dispute any portion of an invoice within thirty (30) days; disputed amounts shall not be due until resolved."',
    sampleExplanation:
      "Net-90 with a 30-day dispute window means you might wait 4+ months. Push for net-30, with a tight dispute window and late-payment interest.",
    redFlags: [
      "Net-60 or longer",
      "No late-payment interest",
      "Long or undefined dispute resolution window",
      "'Pay when paid' (you're paid only when client's customer pays)",
      "Required milestones the client controls",
    ],
    greenFlags: [
      "Net-15 or net-30",
      "1.5%/month late interest",
      "Defined invoice format and dispute window (10 days)",
      "Deposit upfront",
    ],
    negotiationTips: [
      "Insist on net-30 or shorter",
      "Add late-payment interest of 1.5%/month",
      "Require a 25–50% deposit",
      "Cap dispute window at 10 days",
    ],
    faq: [
      {
        q: "What does 'net-30' mean?",
        a: "Payment is due within 30 days of invoice date. Net-60 = 60 days, net-90 = 90 days.",
      },
      {
        q: "Can I charge late fees?",
        a: "Only if the contract allows it. Common rate is 1–1.5% per month on overdue balances.",
      },
    ],
    relatedTools: [T.freelance, T.contractor],
    relatedClauses: [
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Indemnification Clause", slug: "indemnification-clause" },
    ],
  },
  {
    slug: "kill-fee-clause",
    category: "general",
    title: "Kill Fee Clause: What You Get Paid If a Project Is Cancelled",
    shortTitle: "Kill Fee Clause",
    metaTitle: "Kill Fee Clause Explained — legaldecoder",
    metaDescription:
      "What a kill fee is, how much it should be, and how to write one that actually protects your income if a client walks away mid-project.",
    intro:
      "If a client cancels mid-project, the kill fee is the only thing standing between you and unpaid time.",
    whatItIs:
      "A kill fee is a pre-agreed payment owed if a client cancels a project before completion. It's separate from payment for work already delivered — it compensates for the opportunity cost of the cancelled work.",
    whyItMatters:
      "Without a kill fee, a same-day cancellation can leave you with nothing for time blocked off, opportunities turned down, or partial drafts not yet delivered.",
    sampleLanguage:
      '"If Client cancels the Project after kickoff, Client shall pay (a) all fees for work completed and in progress, and (b) a kill fee equal to fifty percent (50%) of the remaining contract value."',
    sampleExplanation:
      "Strong protection. Some contracts use a sliding scale (25% before midpoint, 50% after) — also fair.",
    redFlags: [
      "No kill fee at all",
      "Cancellation only triggers payment for delivered work",
      "Vague 'reasonable compensation' language",
      "Kill fee waived for any reason",
    ],
    greenFlags: [
      "Flat 25–50% kill fee",
      "Or sliding scale by milestone",
      "Plus payment for in-progress work",
    ],
    negotiationTips: [
      "Ask for 50% kill fee post-kickoff",
      "Or scale: 25% before midpoint, 50% after",
      "Pair with notice requirement (14 days)",
    ],
    faq: [
      {
        q: "Are kill fees standard?",
        a: "In creative work, yes — writing, design, photography contracts almost always include them. In other industries it's a negotiation point.",
      },
    ],
    relatedTools: [T.freelance, T.contractor],
    relatedClauses: [
      { name: "Termination for Convenience Clause", slug: "termination-for-convenience-clause" },
      { name: "Payment Terms Clause", slug: "payment-terms-clause" },
    ],
  },
  {
    slug: "usage-rights-clause",
    category: "general",
    title: "Usage Rights Clause: How Your Work Can Be Used After You Deliver",
    shortTitle: "Usage Rights Clause",
    metaTitle: "Usage Rights Clause Explained — legaldecoder",
    metaDescription:
      "How usage-rights clauses define where, how long, and on what your delivered work can be used — and how broad rights cost you future revenue.",
    intro:
      "An IP transfer says who owns it. A usage-rights clause says where it can appear and for how long.",
    whatItIs:
      "A usage-rights clause defines the scope of how a client can use your delivered work: media (print, digital, broadcast), territory (one country vs. worldwide), duration (one campaign vs. perpetual), and exclusivity.",
    whyItMatters:
      "Selling 'unlimited worldwide perpetual exclusive' rights once costs you every future licensing opportunity. Tighter scope means more revenue over time.",
    sampleLanguage:
      '"Client receives a worldwide, perpetual, exclusive, royalty-free license to use the Deliverables in any medium now known or later developed."',
    sampleExplanation:
      "Maximum rights for a single payment. For creative work especially, push for limited media, territory, duration, and non-exclusive use.",
    redFlags: [
      "Worldwide + perpetual + exclusive without an uplift",
      "'Any medium now known or later developed'",
      "No carve-out for portfolio use",
      "Includes derivative works without extra payment",
    ],
    greenFlags: [
      "Defined media (e.g., digital social only)",
      "Defined term (1–3 years)",
      "Non-exclusive license",
      "Portfolio use carve-out",
      "Premium for expanded rights",
    ],
    negotiationTips: [
      "Charge a usage uplift for worldwide / perpetual / exclusive",
      "Limit to specific media and territory",
      "Always carve out portfolio use",
    ],
    faq: [
      {
        q: "What's the difference between IP assignment and usage rights?",
        a: "Assignment transfers ownership outright. A usage license keeps you as the owner and grants a defined scope of use.",
      },
    ],
    relatedTools: [T.freelance, T.contractor],
    relatedClauses: [
      { name: "IP Assignment Clause", slug: "ip-assignment-clause" },
      { name: "Confidentiality Clause", slug: "confidentiality-clause" },
    ],
  },
];

export const CLAUSES_BY_SLUG: Record<string, Clause> = Object.fromEntries(
  CLAUSES.map((c) => [c.slug, c]),
);

export function getClausesByCategory(category: Clause["category"]): Clause[] {
  return CLAUSES.filter((c) => c.category === category);
}
